import { Fragment, useMemo, useState } from 'react';
import { Button, Col, Form, InputGroup, Row, Table } from 'react-bootstrap';
import { ocrSignal } from './ocr.signal';
import {
  chunkCalcapUseOnlyFields,
  chunkCcapsediOwnedBusinessFields,
  chunkFieldsForBorrowerOwnershipRow,
  columnIsInput,
  displayHeader,
  formatOcrDataForSubmit,
  formatOcrLabel,
  formatOcrValue,
  formatPercentValueForDisplay,
  getCustomCategory,
  isOcrPercentFieldName,
  getCustomLabel,
  getOcrCategoriesToRender,
  OCR_SELECT_OPTIONS,
  onOcrCheckboxChange,
  onOcrFieldChange,
  onOcrTableFieldChange,
  renderAdditionalText,
  shouldIncludeOcrFieldForDisplay,
} from './ocr.helpers';
import { FieldComponent } from '../Field/Field';

const OCRRender = ({ readOnly = false }) => {
  const { parsedOcrData, formattedOcrData, modelId } = ocrSignal.value;
  const [focusedPercentageFields, setFocusedPercentageFields] = useState(
    () => new Set()
  );
  const [percentageFieldRawValues, setPercentageFieldRawValues] = useState(
    () => ({})
  );
  const displayCategories = useMemo(
    () => getOcrCategoriesToRender(parsedOcrData, modelId),
    [parsedOcrData, modelId]
  );
  const changesAreSaved = Object.keys(formattedOcrData).length > 0;
  return (
    <div>
      <Row className="mb-16">
        <Col>
          <h5>Application Form</h5>
          <p className="text-muted">
            {readOnly
              ? 'View application form data below.'
              : `Review and edit the fields below. Click "Confirm" after making changes.`}
          </p>
        </Col>
        {!readOnly && (
          <Col className="text-right">
            <Button
              variant="primary"
              className="rounded"
              disabled={changesAreSaved}
              onClick={() => formatOcrDataForSubmit()}
            >
              {changesAreSaved ? 'Saved!' : 'Confirm'}
            </Button>
          </Col>
        )}
      </Row>

      <Row>
        {displayCategories.map((category) => {
          const fields = (Array.isArray(parsedOcrData[category])
            ? parsedOcrData[category]
            : []
          ).filter((f) =>
            shouldIncludeOcrFieldForDisplay(f?.name ?? f?.key)
          );
          return (
            <Fragment key={category}>
              <Col md={12} className="mt-4 mb-3">
                <h6
                  className="text-muted border-bottom pb-2 mb-3"
                  style={{ fontWeight: 600 }}
                >
                  {getCustomCategory(category)}
                </h6>
              </Col>
              {(category === 'borrowerinformation'
                ? chunkFieldsForBorrowerOwnershipRow(fields)
                : category === 'ccapsediownedbusiness'
                  ? chunkCcapsediOwnedBusinessFields(fields)
                  : category === 'calcapuseonly'
                    ? chunkCalcapUseOnlyFields(fields)
                    : fields.map((f) => ({ kind: 'field', field: f }))
              ).map((chunk, chunkIdx) => {
                const renderFieldInner = (fieldDef) => {
                  const fieldName = fieldDef?.name ?? fieldDef?.key;
                  const customLabel = getCustomLabel(fieldName);
                  const label = formatOcrLabel(
                    customLabel || fieldDef?.label
                  );
                  const isMultiSelectDropdown = fieldDef?.type === 'multi-select-dropdown';
                  const baseType = fieldDef?.type || 'text-input';
                  const usePercentInput =
                    baseType === 'percent' || isOcrPercentFieldName(fieldName);

                  /** Match ApplicationDetailsTab: text + % suffix, 0–1 vs percent-point blur handling. */
                  if (usePercentInput) {
                    const rawStored = readOnly
                      ? formattedOcrData[fieldName]
                      : fieldDef?.value;
                    const pctFocused = focusedPercentageFields.has(fieldName);
                    const pctRaw = percentageFieldRawValues[fieldName];
                    const displayValue = readOnly
                      ? formatPercentValueForDisplay(formattedOcrData[fieldName])
                      : pctFocused && pctRaw !== undefined
                        ? pctRaw
                        : formatPercentValueForDisplay(rawStored);

                    return (
                      <div className="w-100">
                        {renderAdditionalText(fieldName)}
                        <Form.Label>{label}</Form.Label>
                        {/*
                          Do not put w-100 on Form.Control inside InputGroup — it forces full row
                          width and wraps the % addon below the field (Bootstrap input-group is flex).
                        */}
                        <InputGroup className="flex-nowrap w-100">
                          <Form.Control
                            type="text"
                            inputMode="decimal"
                            value={displayValue}
                            disabled={readOnly}
                            onFocus={() => {
                              if (readOnly) return;
                              setFocusedPercentageFields((prev) =>
                                new Set(prev).add(fieldName)
                              );
                              setPercentageFieldRawValues((prev) => ({
                                ...prev,
                                [fieldName]: formatPercentValueForDisplay(
                                  fieldDef?.value
                                ),
                              }));
                            }}
                            onBlur={() => {
                              if (readOnly) return;
                              setFocusedPercentageFields((prev) => {
                                const next = new Set(prev);
                                next.delete(fieldName);
                                return next;
                              });
                              const raw = percentageFieldRawValues[fieldName] ?? '';
                              const cleaned = raw.replace(/[%\s]/g, '');
                              setPercentageFieldRawValues((prev) => {
                                const next = { ...prev };
                                delete next[fieldName];
                                return next;
                              });
                              if (cleaned === '') {
                                onOcrFieldChange(
                                  { name: fieldName, value: null },
                                  category
                                );
                                return;
                              }
                              const parsed = parseFloat(cleaned);
                              if (Number.isNaN(parsed)) return;
                              const stored = parsed > 1 ? parsed / 100 : parsed;
                              onOcrFieldChange(
                                { name: fieldName, value: stored },
                                category
                              );
                            }}
                            onChange={(e) => {
                              if (readOnly) return;
                              const s = e.target.value.replace(/[%\s]/g, '');
                              if (s === '' || /^-?\d*\.?\d*$/.test(s)) {
                                setPercentageFieldRawValues((prev) => ({
                                  ...prev,
                                  [fieldName]: s,
                                }));
                              }
                            }}
                            placeholder="0.00"
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                      </div>
                    );
                  }

                  const fieldType = baseType;
                  return (
                    <>
                      {renderAdditionalText(fieldName)}
                      <FieldComponent
                        disabled={readOnly}
                        type={fieldType}
                        label={label}
                        value={
                          readOnly && baseType === 'text-input'
                            ? formatOcrValue(
                              formattedOcrData[fieldName],
                              fieldName,
                              customLabel || fieldDef?.label,
                              null
                            )
                            : readOnly
                              ? formattedOcrData[fieldName]
                              : fieldDef?.value
                        }
                        name={fieldName}
                        options={fieldDef?.options}
                        inputClassName={
                          fieldType === 'select' || fieldType === 'currency'
                            ? 'form-control w-100'
                            : undefined
                        }
                        tableData={fieldDef?.tableData}
                        onChange={
                          fieldDef?.type === 'checkbox'
                            ? (e) => onOcrCheckboxChange(e, category)
                            : isMultiSelectDropdown
                              ? (value) => onOcrFieldChange({ name: fieldName, value }, category)
                              : (e) => onOcrFieldChange(e, category)
                        }
                      />
                    </>
                  );
                };

                const renderFieldCol = (fieldDef) => {
                  const fieldName = fieldDef?.name ?? fieldDef?.key;
                  if (fieldDef?.type === 'table') {
                    return (
                      <Col key={fieldDef.key} md={12} className="mb-3">
                        {renderAdditionalText(fieldName)}
                        <OCRTextInputTable
                          category={category}
                          fieldName={fieldName}
                          fieldDef={fieldDef}
                          readOnly={readOnly}
                          formattedOcrData={formattedOcrData}
                        />
                      </Col>
                    );
                  }
                  const isMultiSelectDropdown = fieldDef?.type === 'multi-select-dropdown';
                  return (
                    <Col
                      key={fieldDef.key}
                      md={isMultiSelectDropdown ? 12 : 6}
                      lg={isMultiSelectDropdown ? 12 : 4}
                      className="mb-3"
                    >
                      {renderFieldInner(fieldDef)}
                    </Col>
                  );
                };

                if (chunk.kind === 'calcapFundRow') {
                  return (
                    <Col
                      key={`calcap-fund-row-${chunkIdx}`}
                      md={12}
                      className="mb-3"
                    >
                      <Row className="g-3 align-items-start">
                        {chunk.fields.map((fieldDef) => (
                          <Col
                            key={fieldDef.key}
                            xs={12}
                            sm={6}
                            md={4}
                            lg={4}
                            className="mb-3"
                          >
                            {renderFieldInner(fieldDef)}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  );
                }

                if (chunk.kind === 'calcapPercentRow') {
                  return (
                    <Col
                      key={`calcap-percent-row-${chunkIdx}`}
                      md={12}
                      className="mb-3"
                    >
                      <Row className="g-3 align-items-start">
                        {chunk.fields.map((fieldDef) => (
                          <Col
                            key={fieldDef.key}
                            xs={12}
                            sm={6}
                            md={4}
                            lg={4}
                            className="mb-3"
                          >
                            {renderFieldInner(fieldDef)}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  );
                }

                if (chunk.kind === 'ownership') {
                  const n = chunk.fields.length;
                  const colMd = Math.min(12, Math.max(2, Math.floor(12 / n)));
                  return (
                    <Col
                      key={`borrower-ownership-${chunk.fields[0]?.key ?? chunkIdx}`}
                      md={12}
                      className="mb-3"
                    >
                      <Row className="g-3">
                        {chunk.fields.map((fieldDef) => (
                          <Col
                            key={fieldDef.key}
                            xs={12}
                            sm={6}
                            md={colMd}
                            lg={colMd}
                            className="mb-3"
                          >
                            {renderFieldInner(fieldDef)}
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  );
                }

                if (chunk.kind === 'sediCheckboxGrid') {
                  const perRow = 3;
                  const rows = [];
                  for (let i = 0; i < chunk.fields.length; i += perRow) {
                    rows.push(chunk.fields.slice(i, i + perRow));
                  }
                  return (
                    <Col
                      key={`ccapsedi-checkbox-grid-${chunkIdx}`}
                      md={12}
                      className="mb-4 pb-3 border-bottom"
                    >
                      {rows.map((rowFields, rowIdx) => (
                        <Row
                          key={`ccapsedi-checkbox-row-${rowIdx}`}
                          className="g-3 align-items-start"
                        >
                          {rowFields.map((fieldDef) => (
                            <Col
                              key={fieldDef.key}
                              xs={12}
                              sm={6}
                              md={4}
                              lg={4}
                              className="mb-3"
                            >
                              {renderFieldInner(fieldDef)}
                            </Col>
                          ))}
                        </Row>
                      ))}
                    </Col>
                  );
                }

                return renderFieldCol(chunk.field);
              })}
            </Fragment>
          );
        })}
      </Row>


      {!readOnly && changesAreSaved && (
        <Row className="mt-4">
          <Col>
            <div className="alert alert-info">
              Form data confirmed. Click &quot;Confirm&quot; again to refresh.
            </div>
          </Col>
        </Row>
      )}

      {!readOnly && (
        <Col className="text-center">
          <Button
            variant="primary"
            className="rounded"
            disabled={changesAreSaved}
            onClick={() => formatOcrDataForSubmit()}
          >
            {changesAreSaved ? 'Saved!' : 'Confirm'}
          </Button>
        </Col>
      )}
    </div>
  );
};

const OCRTextInputTable = ({
  category,
  fieldName,
  fieldDef,
  readOnly,
  formattedOcrData,
}) => (
  <div className="border p-16 mt-16">
    <b>{formatOcrLabel(fieldDef?.label)}</b>
    <Table>
      {!fieldDef?.value?.headers?.every(
        (header) => header === 'Item' || header === 'Value'
      ) && (
          <thead>
            <tr>
              {fieldDef?.value?.headers?.map(
                (header) => {
                  if (displayHeader(header)) {
                    return <th key={header}>{header}</th>;
                  } else {
                    return <th key={header}> </th>;
                  }
                }
              )}
            </tr>
          </thead>
        )}
      <tbody>
        {fieldDef?.value?.rows?.map(
          (rowObj, index) => (
            <tr key={`${fieldName}-${index}`}>
              {fieldDef?.value?.headers?.map(
                (header) => {
                  if (columnIsInput(header)) {
                    return (
                      <td key={`${header}-${index}`} style={{ minWidth: 100 }}>
                        <FieldComponent
                          disabled={readOnly}
                          inputClassName="form-control"
                          type={
                            fieldName.includes('requiredDocuments')
                              ? 'select'
                              : 'text-input'
                          }
                          labelClassName="d-none"
                          value={
                            readOnly
                              ? formatOcrValue(
                                formattedOcrData[fieldName][index][header] || '',
                                fieldName,
                                null,
                                header
                              )
                              : rowObj[header] || ''
                          }
                          options={
                            fieldName.includes('requiredDocuments')
                              ? OCR_SELECT_OPTIONS.requiredDocuments
                              : []
                          }
                          name={header}
                          onChange={(e) =>
                            onOcrTableFieldChange(e, category, fieldName, index)
                          }
                        />
                      </td>
                    );
                  } else {
                    return (
                      <td key={`${header}-${index}`}>
                        {readOnly
                          ? formatOcrValue(
                            formattedOcrData[fieldName]?.[index]?.[header] || rowObj[header] || '',
                            fieldName,
                            null,
                            header
                          )
                          : rowObj[header] || ''}
                      </td>
                    );
                  }
                }
              )}
            </tr>
          )
        )}
      </tbody>
    </Table>
  </div>
);

export default OCRRender;
